import Card from "components/card";
import React from "react";
import { FaCheck, FaBox, FaTruck, FaHome } from "react-icons/fa";
import { RiErrorWarningFill } from "react-icons/ri";
import { useSelector } from "react-redux";

const iconMap = {
  draft: FaBox,
  placed: FaBox,
  processing: FaTruck,
  fulfilled: FaTruck,
  delivered: FaHome,
  cancelled: FaHome,
};

const SkeletonStep = () => (
  <div className="relative flex animate-pulse flex-col items-center">
    <div className="z-10 h-12 w-12 rounded-full bg-gray-300"></div>
    <div className="mt-3 h-4 w-16 rounded bg-gray-300"></div>
    <div className="mt-1 h-3 w-20 rounded bg-gray-300"></div>
  </div>
);

const OrderTrackingView = () => {
  const {
    orderDetailsData,
    orderDetailsLoading,
    orderDetailsError,
    orderStatusError,
    orderStatusData,
    orderStatusLoading,
  } = useSelector((state) => state.orders);

  const isLoading = orderStatusLoading || orderDetailsLoading;

  if (isLoading) {
    return (
      <Card extra={"w-full h-full p-3"}>
        <div className="mb-4 mt-2 flex w-full items-center justify-between">
          <h4 className="px-2 text-xl font-bold text-navy-700 dark:text-white">
            Track Order Details
          </h4>
        </div>
        <div className="grid grid-cols-1 px-2">
          <div className="relative flex justify-between">
            {Array(5)
              .fill(0)
              .map((_, idx) => (
                <SkeletonStep key={idx} />
              ))}
          </div>
        </div>
      </Card>
    );
  }

  if (orderDetailsError || orderStatusError) {
    return (
      <Card extra={"w-full h-full p-3"}>
        <div className="flex items-center gap-2 text-red-500">
          <RiErrorWarningFill className="h-4 w-4" />
          <p className="text-sm">
            {orderDetailsError || orderStatusError || "Something went wrong!"}
          </p>
        </div>
      </Card>
    );
  }

  const currentStatus = orderDetailsData.status?.enumVal;

  const trackingSteps = orderStatusData.map((statusObj, index) => {
    const isCompleted =
      orderStatusData.findIndex((s) => s.enumVal === currentStatus) >= index;

    return {
      id: statusObj._id,
      title: statusObj.name,
      status: isCompleted ? "completed" : "pending",
      icon: iconMap[statusObj.enumVal] || FaBox,
    };
  });

  return (
    <Card extra={"w-full h-full p-3"}>
      {/* Header */}
      <div className="mb-4 mt-2 flex w-full items-center justify-between">
        <h4 className="px-2 text-xl font-bold text-navy-700 dark:text-white">
          Track Order Details
        </h4>
      </div>

      {/* Grid of details */}
      <div className="grid grid-cols-1 px-2">
        <div className="relative">
          {/* Progress Line */}
          <div className="absolute left-6 right-6 top-6 h-0.5 bg-green-500"></div>

          {/* Steps Container */}
          <div className="relative flex justify-between">
            {trackingSteps.map((step, index) => (
              <div
                key={step.id}
                className="relative flex flex-col items-center"
              >
                <div
                  className={`z-10 flex h-12 w-12 items-center justify-center rounded-full ${
                    step.status === "completed"
                      ? "bg-green-500 text-white"
                      : "bg-gray-200 text-gray-400"
                  }`}
                >
                  {step.status === "completed" &&
                  index ===
                    trackingSteps.findIndex((s) => s.status !== "completed") -
                      1 ? (
                    <FaCheck size={20} />
                  ) : (
                    <step.icon size={20} />
                  )}
                </div>

                <div className="mt-3 text-center">
                  <div className="max-w-20 text-center text-sm leading-tight text-gray-500">
                    {step.title}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default OrderTrackingView;
